import React, { useState } from 'react';
import { Plus, Edit2, Trash2, Tag, Loader2 } from 'lucide-react';
import { AdminAPI, resolveImage } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import ImageUpload from './ImageUpload';

const BrandsTab = ({ brands, onRefresh, loading }) => {
  const { toast } = useToast();
  const [editing, setEditing] = useState(null); // null | 'new' | brand
  const [form, setForm] = useState({ name: '', logo: '' });
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState('');

  const filtered = brands.filter(b => !q || b.name.toLowerCase().includes(q.toLowerCase()));

  const openNew = () => { setForm({ name: '', logo: '' }); setEditing('new'); };
  const openEdit = (b) => { setForm({ name: b.name, logo: b.logo || '' }); setEditing(b); };
  const close = () => { setEditing(null); setForm({ name: '', logo: '' }); };

  const save = async () => {
    if (!form.name.trim()) { toast({ title: 'Nom requis' }); return; }
    setBusy(true);
    try {
      if (editing === 'new') {
        await AdminAPI.createBrand({ name: form.name.trim(), logo: form.logo || null });
        toast({ title: '✅ Marque créée' });
      } else {
        await AdminAPI.updateBrand(editing.name, { name: form.name.trim(), logo: form.logo || null });
        toast({ title: '✅ Marque mise à jour' });
      }
      close();
      onRefresh();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Sauvegarde échouée' });
    } finally { setBusy(false); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Supprimer la marque "${b.name}" ?`)) return;
    try {
      await AdminAPI.deleteBrand(b.name);
      toast({ title: 'Marque supprimée' });
      onRefresh();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Suppression échouée' });
    }
  };

  return (
    <div>
      <div className="flex gap-3 mb-4">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher une marque..." className="flex-1" data-testid="admin-brands-search" />
        <Button onClick={openNew} className="bg-[#1F4D3F] hover:bg-[#173B30] text-white whitespace-nowrap" data-testid="admin-brands-new">
          <Plus className="w-4 h-4 mr-1" /> Nouvelle marque
        </Button>
      </div>

      {loading ? (
        <div className="p-10 text-center text-[#7A6F5C]"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Chargement...</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((b) => (
            <div key={b.name} className="bg-white border border-[#E8E0D0] rounded-xl p-4 flex items-center gap-3" data-testid={`admin-brand-card-${b.name}`}>
              <div className="w-14 h-14 rounded-lg bg-[#FAF7F2] flex items-center justify-center overflow-hidden flex-shrink-0">
                {b.logo ? <img src={resolveImage(b.logo)} alt="" className="w-full h-full object-contain" onError={e => { e.target.style.display = 'none'; }} /> : <Tag className="w-6 h-6 text-[#C9BFAE]" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-[#2D2A26] truncate">{b.name}</div>
                <div className="text-xs text-[#7A6F5C]">{b.products_count} produit{b.products_count > 1 ? 's' : ''}</div>
              </div>
              <button onClick={() => openEdit(b)} className="p-1.5 hover:bg-[#F5F1EA] rounded text-[#1F4D3F]" title="Modifier" data-testid={`admin-brand-edit-${b.name}`}><Edit2 className="w-4 h-4" /></button>
              <button onClick={() => remove(b)} className="p-1.5 hover:bg-rose-50 rounded text-rose-600" title="Supprimer" data-testid={`admin-brand-delete-${b.name}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full p-10 text-center text-[#7A6F5C] bg-white border border-[#E8E0D0] rounded-xl">Aucune marque</div>
          )}
        </div>
      )}

      <Dialog open={editing !== null} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing === 'new' ? 'Nouvelle marque' : 'Modifier la marque'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm text-[#2D2A26] mb-1.5 block">Nom *</label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-brand-form-name" />
              {editing !== 'new' && editing?.products_count > 0 && (
                <div className="text-xs text-amber-700 mt-1">⚠️ {editing.products_count} produit{editing.products_count > 1 ? 's' : ''} seront mis à jour automatiquement</div>
              )}
            </div>
            <ImageUpload value={form.logo} onChange={(url) => setForm({ ...form, logo: url })} label="Logo" testId="admin-brand-form-logo" />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={close}>Annuler</Button>
            <Button onClick={save} disabled={busy} className="bg-[#1F4D3F] hover:bg-[#173B30] text-white" data-testid="admin-brand-form-save">
              {busy ? 'Sauvegarde...' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BrandsTab;
